import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Modal, Form, Alert } from 'react-bootstrap';
import { Plus, Edit, Trash2, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { dutyStationService } from '../services/dutyStationService';

const DutyStationManagement = () => {
    const { t, i18n } = useTranslation('dutyStation');
    const [dutyStations, setDutyStations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [currentDutyStation, setCurrentDutyStation] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        is_active: true
    });
    const [validated, setValidated] = useState(false);

    // Debug translations
    console.log(t('currentLanguage'), i18n.language);
    console.log(t('dutystationTranslations'), i18n.getResourceBundle(i18n.language, 'dutyStation'));
    console.log(t('titleTranslation'), t('title'));

    // Fetch duty stations
    const fetchDutyStations = async () => {
        try {
            setLoading(true);
            const data = await dutyStationService.getDutyStations();
            setDutyStations(data);
            setError(null);
        } catch (err) {
            setError(t('errors.load_failed') || t('errorLoadingDutyStations'));
            console.error(t('errorFetchingDutyStations'), err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDutyStations();
    }, []);

    // Handle input change
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value
        });
    };

    // Show add modal
    const handleShowAddModal = () => {
        setFormData({
            name: '',
            is_active: true
        });
        setValidated(false);
        setShowAddModal(true);
    };

    // Show edit modal
    const handleShowEditModal = (dutyStation) => {
        setCurrentDutyStation(dutyStation);
        setFormData({
            name: dutyStation.name,
            is_active: dutyStation.is_active
        });
        setValidated(false);
        setShowEditModal(true);
    };

    // Show delete modal
    const handleShowDeleteModal = (dutyStation) => {
        setCurrentDutyStation(dutyStation);
        setShowDeleteModal(true);
    };

    // Submit form
    const handleSubmit = async (e) => {
        e.preventDefault();
        const form = e.currentTarget;

        if (form.checkValidity() === false) {
            e.stopPropagation();
            setValidated(true);
            return;
        }

        try {
            if (showAddModal) {
                await dutyStationService.createDutyStation(formData);
                setShowAddModal(false);
            } else if (showEditModal && currentDutyStation) {
                await dutyStationService.updateDutyStation(currentDutyStation.id, formData);
                setShowEditModal(false);
            }
            fetchDutyStations();
        } catch (err) {
            setError(t('errors.save_failed') || t('errorSavingDutyStation'));
            console.error(t('errorSavingDutyStation'), err);
        }
    };

    // Delete duty station
    const handleDelete = async () => {
        try {
            if (currentDutyStation) {
                await dutyStationService.deleteDutyStation(currentDutyStation.id);
                setShowDeleteModal(false);
                fetchDutyStations();
            }
        } catch (err) {
            setError(t('errors.delete_failed') || t('errorDeletingDutyStation'));
            console.error(t('errorDeletingDutyStation'), err);
        }
    };

    // Toggle duty station status
    const handleToggleStatus = async (dutyStation) => {
        try {
            await dutyStationService.updateDutyStationStatus(dutyStation.id, !dutyStation.is_active);
            fetchDutyStations();
        } catch (err) {
            setError(t('errors.status_update_failed') || t('errorUpdatingDutyStationStatus'));
            console.error(t('errorUpdatingDutyStationStatus'), err);
        }
    };

    return (
        <Container className="py-4">
            <Row className="mb-4">
                <Col>
                    <h1>{t('title') || t('dutyStationManagement')}</h1>
                    <p className="text-muted">
                        {t('subtitle') || t('manageDutyStationsForStaffAssignments')}
                    </p>
                </Col>
                <Col xs="auto" className="d-flex align-items-center">
                    <Button
                        variant="outline-secondary"
                        className="me-2"
                        onClick={fetchDutyStations}
                    >
                        <RefreshCw size={18} />
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleShowAddModal}
                    >
                        <Plus size={18} className="me-1" />
                        {t('buttons.add') || t('addDutyStation')}
                    </Button>
                </Col>
            </Row>

            {error && (
                <Alert variant="danger" onClose={() => setError(null)} dismissible>
                    {error}
                </Alert>
            )}

            <Card>
                <Card.Body>
                    {loading ? (
                        <div className="text-center py-4">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">{t('loading')}</span>
                            </div>
                        </div>
                    ) : (
                        <Table responsive hover>
                            <thead>
                            <tr>
                                <th>{t('table.name') || 'Name'}</th>
                                <th>{t('table.status') || t('status')}</th>
                                <th className="text-end">{t('table.actions') || t('actions')}</th>
                            </tr>
                            </thead>
                            <tbody>
                            {dutyStations.length === 0 ? (
                                <tr>
                                    <td colSpan="3" className="text-center py-3">
                                        {t('table.no_duty_stations') || t('noDutyStationsFound')}
                                    </td>
                                </tr>
                            ) : (
                                dutyStations.map((dutyStation) => (
                                    <tr key={dutyStation.id}>
                                        <td>{dutyStation.name}</td>
                                        <td>
                                            <span className={`badge bg-${dutyStation.is_active ? 'success' : 'danger'}`}>
                                                {dutyStation.is_active ?
                                                    (t('table.active') || t('active')) :
                                                    (t('table.inactive') || t('inactive'))}
                                            </span>
                                        </td>
                                        <td className="text-end">
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                className="me-2"
                                                onClick={() => handleToggleStatus(dutyStation)}
                                            >
                                                {dutyStation.is_active ?
                                                    (t('buttons.deactivate') || t('deactivate')) :
                                                    (t('buttons.activate') || t('activate'))}
                                            </Button>
                                            <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                className="me-2"
                                                onClick={() => handleShowEditModal(dutyStation)}
                                            >
                                                <Edit size={16} />
                                            </Button>
                                            <Button
                                                variant="outline-danger"
                                                size="sm"
                                                onClick={() => handleShowDeleteModal(dutyStation)}
                                            >
                                                <Trash2 size={16} />
                                            </Button>
                                        </td>
                                    </tr>
                                ))
                            )}
                            </tbody>
                        </Table>
                    )}
                </Card.Body>
            </Card>

            {/* Add Modal */}
            <Modal show={showAddModal} onHide={() => setShowAddModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>{t('modals.add.title') || t('addDutyStation')}</Modal.Title>
                </Modal.Header>
                <Form noValidate validated={validated} onSubmit={handleSubmit}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>{t('form.name') || 'Name'}</Form.Label>
                            <Form.Control
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                required
                            />
                            <Form.Control.Feedback type="invalid">
                                {t('form.name_required') || t('nameIsRequired')}
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowAddModal(false)}>
                            {t('buttons.cancel') || t('cancel')}
                        </Button>
                        <Button variant="primary" type="submit">
                            {t('buttons.save') || 'Save'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Edit Modal */}
            <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>{t('modals.edit.title') || t('editDutyStation')}</Modal.Title>
                </Modal.Header>
                <Form noValidate validated={validated} onSubmit={handleSubmit}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>{t('form.name') || 'Name'}</Form.Label>
                            <Form.Control
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                required
                            />
                            <Form.Control.Feedback type="invalid">
                                {t('form.name_required') || t('nameIsRequired')}
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowEditModal(false)}>
                            {t('buttons.cancel') || t('cancel')}
                        </Button>
                        <Button variant="primary" type="submit">
                            {t('buttons.save') || 'Save'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Delete Modal */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>{t('modals.delete.title') || t('deleteDutyStation')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {t('modals.delete.confirmation') || t('areYouSureYouWantToDeleteThisDutyStation')}{' '}
                    {currentDutyStation && (
                        <strong>{currentDutyStation.name}</strong>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                        {t('buttons.cancel') || t('cancel')}
                    </Button>
                    <Button variant="danger" onClick={handleDelete}>
                        {t('buttons.delete') || t('delete')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default DutyStationManagement;